import React, { useCallback, useEffect, useRef, useState } from 'react';

import { selectThreadMessagesResult, useAppSelector } from '@fly/store';

import styles from '../../FlyChat.module.less';
import { FlyChatMessage } from '../FlyChatMessage/FlyChatMessage';
import { FlyChatError } from '../index';

interface FlyChatContentProps {
  messageIds?: string[];
  isStreaming?: boolean;
  onRetry: () => void;
  errorMessage?: string | null;
  threadId?: string;
}

export const FlyChatContent: React.FC<FlyChatContentProps> = ({
  messageIds = [],
  isStreaming = false,
  onRetry,
  errorMessage = null,
  threadId,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isFirstScroll, setIsFirstScroll] = useState(true);
  const lastMessage = useAppSelector((state) => {
    if (!threadId) return null;
    const messages = selectThreadMessagesResult(state, threadId).data;
    return messages?.at(-1) ?? null;
  });
  const streamingStepCount = lastMessage?.streamingSteps?.length ?? 0;
  const lastMessageContentLength = lastMessage?.content.length ?? 0;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: isFirstScroll ? 'instant' : 'smooth',
      block: 'end',
    });

    if (isFirstScroll) {
      setIsFirstScroll(false);
    }
  }, [isFirstScroll]);

  useEffect(() => {
    scrollToBottom();
  }, [
    messageIds.length,
    isStreaming,
    streamingStepCount,
    lastMessageContentLength,
    scrollToBottom,
  ]);

  const containerClasses = [styles.chatMessagesContainer, 'text-size-fly-3!'];
  if (errorMessage && errorMessage.length) {
    containerClasses.push(styles.error);
  }
  return (
    <div className={containerClasses.join(' ')} data-testid='chat-container'>
      {messageIds.map((messageId) => (
        <FlyChatMessage
          key={messageId}
          messageId={messageId}
          threadId={threadId}
        />
      ))}
      {errorMessage && errorMessage.length > 0 && !isStreaming && (
        <FlyChatError onRetry={onRetry} />
      )}
      <div ref={messagesEndRef} />
    </div>
  );
};
